// The active typing surface — now round-based.
//
// One lesson = N rounds. Between every round we show an intermission card
// with the round result and an explicit "Continue" button, so the user
// feels they are completing small tasks instead of typing endlessly.
//
// Important behavior changes vs. the original MVP:
//
//   - STRICT mode: a wrong key counts as a mistake but does NOT advance
//     the cursor. The user must type the expected character to move on.
//     This is what "active recall" really needs.
//
//   - Hesitation-based hints (Recall mode):
//       0 - 1499ms idle  : no hint
//       >= 1500ms idle   : soft highlight on the keyboard
//       >= 3000ms idle   : also show finger name under the target
//
//   - Challenge mode: no hints at all unless the user makes >= 3 mistakes
//     in the round, at which point we switch to soft highlight only.
//
//   - Per-key response time is recorded as ms-since-cursor-arrived for
//     every advancing keystroke.

import { useCallback, useMemo, useRef, useState } from 'react';
import type {
  KeyboardLayoutId,
  PracticeMode,
  RoundResult,
  Trin,
  UserPreferencesPatch,
  UserProfile,
} from '../types';
import { trinLabel, trinUsesStrictCase } from '../lib/curriculum';
import {
  presentationKindFromRound,
  presentationStyle,
} from '../lib/presentationLabels';
import {
  CUSTOM_PRACTICE_TRIN_ID,
  isExtraTrinId,
  MENTAL_IMAGERY_TRIN_ID,
  WEAK_SPOTS_TRIN_ID,
} from '../lib/extraRounds';
import {
  buildFlowPracticeTrin,
  effectiveFlowLexicon,
  FLOW_PRACTICE_TRIN_ID,
} from '../lib/flowPractice';
import { buildCustomPracticeTrin } from '../lib/customPractice';
import {
  buildMentalImageryTrin,
  clampMentalImageryRepeats,
} from '../lib/mentalImagery';
import CustomPracticeSetup from './CustomPracticeSetup';
import {
  prepareLesson,
  type PreparedRound,
} from '../lib/lessonGenerator';
import { useEnterAction } from '../lib/useEnterAction';
import { useRoundInput } from '../lib/useRoundInput';
import Keyboard, { fingerNameForKey } from './Keyboard';
import MentalRehearsalCard from './MentalRehearsalCard';
import RoundIntermission from './RoundIntermission';
import FlowPracticeSetup from './FlowPracticeSetup';
import TrinIntroCard from './TrinIntroCard';
import DictationRound from './rounds/DictationRound';
import EchoRound from './rounds/EchoRound';
import MetronomeRound from './rounds/MetronomeRound';
import ShadowRound from './rounds/ShadowRound';
import ImageryRound from './rounds/ImageryRound';
import FlowRound from './rounds/FlowRound';
import MentalImageryIntro from './MentalImageryIntro';
import ImageryPreRound from './ImageryPreRound';
import ImageryIntermission from './ImageryIntermission';
import StoryRound from './rounds/StoryRound';
import StoryRound2 from './rounds/StoryRound2';
import MetronomeBpmPicker from './MetronomeBpmPicker';
import MetronomeModePicker from './MetronomeModePicker';
import DictationLexiconToggle from './DictationLexiconToggle';
import {
  effectiveMetronomeBpmForUser,
  isMetronomeBpmCustom,
  suggestedMetronomeBpmForUser,
} from '../lib/presentationTempo';
import WeakSpotsIntro from './WeakSpotsIntro';
import Niveau4LexiconToggle from './Niveau4LexiconToggle';
import { effectiveNiveau4Lexicon } from '../lib/niveau4Lexicon';
import { dictationUnitLengthLabel } from '../lib/dictationUnits';
import { defaultPreferences } from '../lib/storage';
import { metronomeSurvivalRecordMs } from '../lib/metronomeSurvival';

interface LessonScreenProps {
  trin: Trin;
  user: UserProfile;
  onFinish: (rounds: RoundResult[], startedAt: string) => void;
  /** Gem resultat og start ny fortælling (uden opsummering). */
  onStoryPlayAnother?: (
    rounds: RoundResult[],
    startedAt: string,
  ) => void;
  onCancel: () => void;
  onPreferencesChange?: (patch: UserPreferencesPatch) => void;
  /** Dev only: skip intro and earlier rounds; open directly at this index. */
  devStartRoundIndex?: number;
}

type Phase =
  | 'briefing'
  | 'custom-setup'
  | 'flow-setup'
  | 'intro'
  | 'rehearsal'
  | 'preround'
  | 'playing'
  | 'intermission';

export default function LessonScreen({
  trin,
  user,
  onFinish,
  onStoryPlayAnother,
  onCancel,
  onPreferencesChange,
  devStartRoundIndex,
}: LessonScreenProps) {
  const [customPracticeKeys, setCustomPracticeKeys] = useState<
    string[] | null
  >(null);
  const [mentalImageryRepeats, setMentalImageryRepeats] = useState(() =>
    clampMentalImageryRepeats(user.preferences?.mentalImageryRepeats ?? 1),
  );

  const [flowLexicon, setFlowLexicon] = useState<ReturnType<typeof effectiveFlowLexicon>>(
    () => effectiveFlowLexicon(user),
  );
  const [flowReady, setFlowReady] = useState(
    () => trin.id !== FLOW_PRACTICE_TRIN_ID,
  );

  const effectiveTrin = useMemo(() => {
    if (trin.id === CUSTOM_PRACTICE_TRIN_ID && customPracticeKeys) {
      return buildCustomPracticeTrin(user, customPracticeKeys) ?? trin;
    }
    if (trin.id === MENTAL_IMAGERY_TRIN_ID) {
      return (
        buildMentalImageryTrin(user, { repeats: mentalImageryRepeats }) ?? trin
      );
    }
    if (trin.id === FLOW_PRACTICE_TRIN_ID && flowReady) {
      return buildFlowPracticeTrin(user, flowLexicon) ?? trin;
    }
    return trin;
  }, [trin, user, customPracticeKeys, mentalImageryRepeats, flowLexicon, flowReady]);

  const lessonPlan = useMemo<PreparedRound[]>(
    () => prepareLesson(effectiveTrin, user),
    [effectiveTrin, user, user.preferences?.niveau4Lexicon],
  );
  const niveau4Lexicon = effectiveNiveau4Lexicon(user);
  const handleNiveau4LexiconChange = useCallback(
    (lexicon: 'da' | 'en') => {
      onPreferencesChange?.({ niveau4Lexicon: lexicon });
    },
    [onPreferencesChange],
  );
  const startedAtRef = useRef<string>(new Date().toISOString());

  const devSkipToRound =
    import.meta.env.DEV && devStartRoundIndex !== undefined;
  const initialRoundIdx = devSkipToRound
    ? Math.max(
        0,
        Math.min(devStartRoundIndex, Math.max(0, lessonPlan.length - 1)),
      )
    : 0;

  // Which round we're on and which phase of that round we're in.
  const [roundIdx, setRoundIdx] = useState(initialRoundIdx);
  const [phase, setPhase] = useState<Phase>(() => {
    if (devSkipToRound) return 'playing';
    if (trin.id === CUSTOM_PRACTICE_TRIN_ID) return 'custom-setup';
    if (trin.id === FLOW_PRACTICE_TRIN_ID) return 'flow-setup';
    if (trin.id === WEAK_SPOTS_TRIN_ID) return 'briefing';
    if (trin.id === MENTAL_IMAGERY_TRIN_ID) return 'briefing';
    if (trin.newKeys.length > 0) return 'intro';
    if (trin.allowAltLayer) return 'intro';
    if (trin.id === 'niveau-5-trin-1') return 'intro';
    return 'preround';
  });
  const mentalRehearsalEnabled =
    user.preferences?.mentalRehearsalEnabled !== false;

  // Accumulated results so far.
  const [results, setResults] = useState<RoundResult[]>([]);

  const handleRoundFinish = useCallback(
    (r: RoundResult) => {
      if (trin.id === FLOW_PRACTICE_TRIN_ID) {
        setResults((prev) => {
          const next = [...prev, r];
          onFinish(next, startedAtRef.current);
          return next;
        });
        return;
      }
      setResults((prev) => [...prev, r]);
      if (r.presentationKind === 'story' || r.presentationKind === 'story2') return;
      setPhase('intermission');
    },
    [trin.id, onFinish],
  );

  const handleStoryComplete = useCallback(
    (r: RoundResult, action: 'dashboard' | 'another') => {
      let next: RoundResult[] = [];
      setResults((prev) => {
        next = [...prev, r];
        return next;
      });
      if (action === 'dashboard') {
        onFinish(next, startedAtRef.current);
      } else {
        onStoryPlayAnother?.(next, startedAtRef.current);
      }
    },
    [onFinish, onStoryPlayAnother],
  );

  const handleContinue = useCallback(() => {
    if (roundIdx + 1 >= lessonPlan.length) {
      onFinish([...results], startedAtRef.current);
      return;
    }
    setRoundIdx(roundIdx + 1);
    setPhase('preround');
  }, [roundIdx, lessonPlan.length, results, onFinish]);

  if (phase === 'custom-setup') {
    return (
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
        <CustomPracticeSetup
          user={user}
          onContinue={(keys) => {
            setCustomPracticeKeys(keys);
            setPhase('preround');
          }}
          onCancel={onCancel}
          onPreferencesChange={onPreferencesChange}
        />
      </div>
    );
  }

  if (phase === 'flow-setup') {
    return (
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
        <FlowPracticeSetup
          user={user}
          lexicon={flowLexicon}
          onLexiconChange={setFlowLexicon}
          onContinue={() => {
            setFlowReady(true);
            setPhase('playing');
          }}
          onCancel={onCancel}
          onPreferencesChange={onPreferencesChange}
        />
      </div>
    );
  }

  if (lessonPlan.length === 0) {
    return (
      <div className="mx-auto max-w-2xl text-center text-faint">
        Dette niveau har ingen runder defineret endnu.
      </div>
    );
  }

  const round = lessonPlan[roundIdx];

  const isMentalImagery = trin.id === MENTAL_IMAGERY_TRIN_ID;

  return (
    <div
      className={`mx-auto flex w-full flex-col gap-6 ${
        isMentalImagery ? 'max-w-2xl' : 'max-w-5xl'
      }`}
    >
      {isMentalImagery ? (
        <ImageryLessonHeader onCancel={onCancel} />
      ) : (
        <LessonHeader
          trin={effectiveTrin}
          round={round}
          onCancel={onCancel}
        />
      )}

      {phase === 'briefing' && trin.id === WEAK_SPOTS_TRIN_ID && (
        <WeakSpotsIntro
          user={user}
          onContinue={() => setPhase('preround')}
          onCancel={onCancel}
        />
      )}

      {phase === 'briefing' && trin.id === MENTAL_IMAGERY_TRIN_ID && (
        <MentalImageryIntro
          repeats={mentalImageryRepeats}
          onRepeatsChange={setMentalImageryRepeats}
          onContinue={() => {
            onPreferencesChange?.({
              mentalImageryRepeats: mentalImageryRepeats,
            });
            setPhase('preround');
          }}
          onCancel={onCancel}
        />
      )}

      {phase === 'intro' && (
        <TrinIntroCard
          trin={effectiveTrin}
          keyboardLayout={user.keyboardLayout}
          unlockedKeys={trin.unlockedKeys}
          niveau4Lexicon={
            trin.tierId === 'niveau-4' ? niveau4Lexicon : undefined
          }
          onNiveau4LexiconChange={
            trin.tierId === 'niveau-4' ? handleNiveau4LexiconChange : undefined
          }
          onContinue={() =>
            setPhase(
              mentalRehearsalEnabled && trin.newKeys.length > 0
                ? 'rehearsal'
                : 'preround',
            )
          }
          onCancel={onCancel}
        />
      )}

      {phase === 'rehearsal' && (
        <MentalRehearsalCard
          trin={effectiveTrin}
          keyboardLayout={user.keyboardLayout}
          onContinue={() => setPhase('preround')}
          onSkip={() => setPhase('preround')}
        />
      )}

      {phase === 'preround' && isMentalImagery && (
        <ImageryPreRound
          round={round}
          onStart={() => setPhase('playing')}
        />
      )}

      {phase === 'preround' && !isMentalImagery && (
        <PreRoundCard
          round={round}
          trinId={trin.id}
          user={user}
          niveau4Lexicon={
            trin.tierId === 'niveau-4' ? niveau4Lexicon : undefined
          }
          onNiveau4LexiconChange={
            trin.tierId === 'niveau-4' && round.index === 0
              ? handleNiveau4LexiconChange
              : undefined
          }
          onPreferencesChange={onPreferencesChange}
          onStart={() => setPhase('playing')}
        />
      )}

      {phase === 'playing' && (
        <RoundPlayer
          key={round.template.id + ':' + roundIdx}
          round={round}
          trin={effectiveTrin}
          user={user}
          onFinish={handleRoundFinish}
          onStoryComplete={handleStoryComplete}
        />
      )}

      {phase === 'intermission' && isMentalImagery && (
        <ImageryIntermission
          round={round}
          result={results[results.length - 1]}
          isLast={roundIdx + 1 >= lessonPlan.length}
          onContinue={handleContinue}
        />
      )}

      {phase === 'intermission' && !isMentalImagery && (
        <RoundIntermission
          round={round}
          result={results[results.length - 1]}
          isLast={
            isExtraTrinId(trin.id) || roundIdx + 1 >= lessonPlan.length
          }
          onContinue={handleContinue}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header — always visible
// ---------------------------------------------------------------------------

interface LessonHeaderProps {
  trin: Trin;
  round: PreparedRound;
  onCancel: () => void;
}

function ImageryLessonHeader({ onCancel }: { onCancel: () => void }) {
  return (
    <div className="flex items-center justify-between">
      <p className="text-sm font-light text-muted">Imaginær skrivning</p>
      <button
        type="button"
        onClick={onCancel}
        className="text-sm text-stone-500 transition hover:text-strong dark:text-stone-400"
      >
        Afslut
      </button>
    </div>
  );
}

function LessonHeader({ trin, round, onCancel }: LessonHeaderProps) {
  const kind = presentationKindFromRound(round.template.presentation);
  const style = presentationStyle(kind);
  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="label">
          {trinLabel(trin)} · Runde {round.index + 1} af {round.total}
          {style && (
            <>
              {' '}
              ·{' '}
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ring-1 ${style.badgeCls}`}
              >
                {style.icon} {style.label}
              </span>
            </>
          )}
        </p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">
          {trin.title}
        </h1>
        <p className="text-sm text-muted">{trin.description}</p>
      </div>
      <button onClick={onCancel} className="btn-secondary">
        Afslut
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pre-round card — shows title, purpose, mode before the round begins
// ---------------------------------------------------------------------------

interface PreRoundCardProps {
  round: PreparedRound;
  trinId: string;
  user: UserProfile;
  niveau4Lexicon?: 'da' | 'en';
  onNiveau4LexiconChange?: (lexicon: 'da' | 'en') => void;
  onPreferencesChange?: (patch: UserPreferencesPatch) => void;
  onStart: () => void;
}

function PreRoundCard({
  round,
  trinId,
  user,
  niveau4Lexicon,
  onNiveau4LexiconChange,
  onPreferencesChange,
  onStart,
}: PreRoundCardProps) {
  const mode = round.template.mode;
  const kind = presentationKindFromRound(round.template.presentation);
  const style = presentationStyle(kind);
  const isMetronome = kind === 'metronome' && !!onPreferencesChange;
  const showMetronomeSurvival = isMetronome && isExtraTrinId(trinId);
  const isDictationUnit =
    kind === 'dictation' &&
    round.template.presentation?.kind === 'dictation' &&
    round.template.presentation.dictationMode === 'unit' &&
    !!onPreferencesChange;
  useEnterAction(onStart);
  const bpm = effectiveMetronomeBpmForUser(user);
  const suggested = suggestedMetronomeBpmForUser(user);
  const dictationLexicon =
    user.preferences?.dictationLexicon ??
    defaultPreferences().dictationLexicon ??
    'da';
  const dictationUnitLength =
    user.preferences?.dictationUnitLength ??
    defaultPreferences().dictationUnitLength ??
    1;
  const metronomeMode =
    user.preferences?.metronomeMode ??
    defaultPreferences().metronomeMode ??
    'classic';
  const survivalRecordMs = metronomeSurvivalRecordMs(user);

  return (
    <div
      className={`surface mx-auto w-full max-w-xl p-6 text-center animate-fade-in-up ${
        style ? `ring-2 ${style.accentRing} ${style.accentBg}` : ''
      }`}
    >
      {style && (
        <p
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs ring-1 ${style.badgeCls}`}
        >
          {style.icon} {style.label}
        </p>
      )}
      <p className={`label ${style ? 'mt-3' : ''}`}>{modeLabelLong(mode)}</p>
      <h2 className="mt-2 text-2xl font-semibold text-strong">
        {round.template.title}
      </h2>
      <p className="mt-2 text-muted">{round.template.purpose}</p>
      {style && (
        <p className="mt-3 text-sm text-strong">{style.preRoundHint}</p>
      )}
      {showMetronomeSurvival && (
        <MetronomeModePicker
          mode={metronomeMode}
          recordMs={survivalRecordMs}
          onModeChange={(mode) => onPreferencesChange!({ metronomeMode: mode })}
        />
      )}
      {isMetronome && (
        <MetronomeBpmPicker
          bpm={bpm}
          suggested={suggested}
          custom={isMetronomeBpmCustom(user)}
          onBpmChange={(v) => onPreferencesChange!({ metronomeBpm: v })}
          onUseSuggested={() => onPreferencesChange!({ metronomeBpm: null })}
        />
      )}
      {niveau4Lexicon !== undefined && onNiveau4LexiconChange && (
        <div className="mt-5 text-left">
          <Niveau4LexiconToggle
            compact
            value={niveau4Lexicon}
            onChange={onNiveau4LexiconChange}
          />
        </div>
      )}
      {isDictationUnit && (
        <div className="mt-5 text-left space-y-3">
          <DictationLexiconToggle
            compact
            value={dictationLexicon}
            onChange={(dictationLexicon) =>
              onPreferencesChange?.({ dictationLexicon })
            }
          />
          <div className="surface p-4">
            <p className="label">Orddiktat · antal ord</p>
            <p className="mt-1 text-xs text-faint">
              {dictationUnitLengthLabel(dictationUnitLength)} ord pr. enhed
            </p>
            <input
              type="range"
              min={1}
              max={7}
              step={1}
              value={dictationUnitLength}
              onChange={(e) =>
                onPreferencesChange?.({
                  dictationUnitLength: Number(e.target.value),
                })
              }
              className="mt-2 w-full accent-indigo-500"
              aria-label="Antal ord pr. enhed"
            />
            <div className="mt-1 flex justify-between text-[10px] text-faint">
              <span>1</span>
              <span>7+</span>
            </div>
          </div>
        </div>
      )}
      <p className="mt-4 text-xs text-faint">{modeHint(mode)}</p>
      <button
        type="button"
        autoFocus
        onClick={onStart}
        className="mt-6 rounded-xl bg-indigo-500 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-indigo-400 dark:shadow-lg dark:shadow-indigo-500/30"
      >
        Start runden
      </button>
      <p className="mt-2 text-xs text-faint">Tryk Enter for at starte</p>
    </div>
  );
}

function modeLabelLong(mode: PracticeMode): string {
  switch (mode) {
    case 'guided':
      return 'Med ledetråde — let tilgang';
    case 'recall':
      return 'Genkald — ledetråde kun ved tøven';
    case 'challenge':
      return 'Udfordring — ingen ledetråde';
  }
}

function modeHint(mode: PracticeMode): string {
  switch (mode) {
    case 'guided':
      return 'Den næste tast lyser hele tiden. Kig på tasterne hvis du har brug for det.';
    case 'recall':
      return 'Tasterne hjælper først, hvis du tøver længere end 1,5 sekund.';
    case 'challenge':
      return 'Ingen hjælp. Lavet til at lukke runden af.';
  }
}

// ---------------------------------------------------------------------------
// Round player — chooses ActiveRound or a presentation-specific variant
// ---------------------------------------------------------------------------

interface RoundPlayerProps {
  round: PreparedRound;
  trin: Trin;
  user: UserProfile;
  onFinish: (r: RoundResult) => void;
  onStoryComplete?: (
    r: RoundResult,
    action: 'dashboard' | 'another',
  ) => void;
}

function RoundPlayer({
  round,
  trin,
  user,
  onFinish,
  onStoryComplete,
}: RoundPlayerProps) {
  const kind = round.template.presentation?.kind ?? 'standard';
  const strictCase = trinUsesStrictCase(trin);
  const allowAltLayer = trin.allowAltLayer === true;
  if (kind === 'dictation') {
    return (
      <DictationRound
        round={round}
        trin={trin}
        keyboardLayout={user.keyboardLayout}
        user={user}
        strictCase={strictCase}
        allowAltLayer={allowAltLayer}
        onFinish={onFinish}
      />
    );
  }
  if (kind === 'echo') {
    return (
      <EchoRound
        round={round}
        trin={trin}
        keyboardLayout={user.keyboardLayout}
        strictCase={strictCase}
        allowAltLayer={allowAltLayer}
        keyboardLayoutId={user.keyboardLayout}
        onFinish={onFinish}
      />
    );
  }
  if (kind === 'metronome') {
    return (
      <MetronomeRound
        round={round}
        trin={trin}
        keyboardLayout={user.keyboardLayout}
        user={user}
        strictCase={strictCase}
        allowAltLayer={allowAltLayer}
        allowSurvivalMode={isExtraTrinId(trin.id)}
        keyboardLayoutId={user.keyboardLayout}
        onFinish={onFinish}
      />
    );
  }
  if (kind === 'shadow') {
    return (
      <ShadowRound
        round={round}
        trin={trin}
        keyboardLayout={user.keyboardLayout}
        strictCase={strictCase}
        allowAltLayer={allowAltLayer}
        keyboardLayoutId={user.keyboardLayout}
        onFinish={onFinish}
      />
    );
  }
  if (kind === 'imagery') {
    return (
      <ImageryRound
        round={round}
        trin={trin}
        keyboardLayout={user.keyboardLayout}
        user={user}
        strictCase={strictCase}
        allowAltLayer={allowAltLayer}
        onFinish={onFinish}
      />
    );
  }
  if (kind === 'flow') {
    return (
      <FlowRound
        round={round}
        trin={trin}
        keyboardLayout={user.keyboardLayout}
        user={user}
        onFinish={onFinish}
      />
    );
  }
  if (kind === 'story' && onStoryComplete) {
    return (
      <StoryRound
        round={round}
        trin={trin}
        keyboardLayout={user.keyboardLayout}
        user={user}
        strictCase={strictCase}
        allowAltLayer={allowAltLayer}
        onComplete={onStoryComplete}
      />
    );
  }
  if (kind === 'story2' && onStoryComplete) {
    return (
      <StoryRound2
        round={round}
        trin={trin}
        keyboardLayout={user.keyboardLayout}
        strictCase={strictCase}
        allowAltLayer={allowAltLayer}
        onComplete={onStoryComplete}
      />
    );
  }
  return (
    <ActiveRound
      round={round}
      trin={trin}
      keyboardLayout={user.keyboardLayout}
      strictCase={strictCase}
      allowAltLayer={allowAltLayer}
      onFinish={onFinish}
    />
  );
}

// ---------------------------------------------------------------------------
// Active round — the actual typing surface (standard presentation)
// ---------------------------------------------------------------------------

interface ActiveRoundProps {
  round: PreparedRound;
  trin: Trin;
  keyboardLayout: KeyboardLayoutId;
  strictCase: boolean;
  allowAltLayer: boolean;
  onFinish: (r: RoundResult) => void;
}

function ActiveRound({
  round,
  trin,
  keyboardLayout,
  strictCase,
  allowAltLayer,
  onFinish,
}: ActiveRoundProps) {
  const targetText = round.targetText;
  const mode = round.template.mode;
  const presentationKind = round.template.presentation?.kind ?? 'standard';

  const input = useRoundInput({
    targetText,
    mode,
    roundId: round.template.id,
    roundTitle: round.template.title,
    presentationKind,
    strictCase,
    allowAltLayer,
    keyboardLayoutId: keyboardLayout,
    onFinish,
  });

  return (
    <>
      <div className="grid grid-cols-4 gap-3">
        <Stat label="Tid" value={formatMs(input.elapsedMs)} />
        <Stat label="WPM" value={input.liveWpm.toFixed(0)} />
        <Stat
          label="Nøjagtighed"
          value={`${(input.liveAcc * 100).toFixed(0)}%`}
        />
        <Stat
          label="Tegn"
          value={`${input.cursor} / ${targetText.length}`}
        />
      </div>

      <TextDisplay
        text={targetText}
        cursor={input.cursor}
        correctMask={input.correctMask}
      />

      <div className="space-y-2">
        <Keyboard
          layoutId={keyboardLayout}
          nextExpectedKey={input.expectedChar}
          lastPressedKey={input.lastPress?.key}
          lastPressCorrect={input.lastPress?.correct}
          unlockedKeys={trin.unlockedKeys}
          hintLevel={input.hintLevel}
          showFingerDot={input.hintLevel !== 'none' && mode !== 'guided'}
        />

        <div className="flex h-6 items-center justify-center text-xs">
          {input.strongHintActive && input.expectedChar && (
            <span className="text-muted animate-fade-in-up">
              Brug{' '}
              {fingerNameForKey(input.expectedChar, keyboardLayout)?.toLowerCase()}
            </span>
          )}
        </div>
      </div>

      <p className="text-center text-xs text-faint">
        Tryk Escape for at afslutte runden tidligt. Forkerte tegn tæller som
        fejl, men du kommer ikke videre før du rammer den rigtige tast.
      </p>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="surface-soft px-4 py-3">
      <p className="label">{label}</p>
      <p className="mt-1 font-mono text-2xl text-strong">{value}</p>
    </div>
  );
}

interface TextDisplayProps {
  text: string;
  cursor: number;
  correctMask: ReadonlyArray<boolean>;
}

function TextDisplay({ text, cursor, correctMask }: TextDisplayProps) {
  // Show a window centered around the cursor so the user's eye stays
  // anchored in roughly the same screen location all round.
  const windowSize = 240;
  const start = Math.max(0, cursor - 40);
  const end = Math.min(text.length, start + windowSize);
  const slice = text.slice(start, end);

  return (
    <div className="surface p-6">
      <p className="whitespace-pre-wrap break-words font-mono text-2xl leading-relaxed tracking-wide">
        {[...slice].map((ch, i) => {
          const absoluteIndex = start + i;
          const isCorrect = correctMask[absoluteIndex];
          const isCursor = absoluteIndex === cursor;
          let cls = 'text-zinc-400 dark:text-zinc-500';
          if (isCorrect) cls = 'text-emerald-600 dark:text-emerald-300';
          else if (isCursor)
            cls =
              'text-zinc-900 dark:text-zinc-100 bg-indigo-100 dark:bg-indigo-500/10 rounded-sm border-l-2 border-indigo-500';
          return (
            <span key={absoluteIndex} className={cls}>
              {ch === ' ' ? '\u00A0' : ch}
            </span>
          );
        })}
      </p>
    </div>
  );
}

function formatMs(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
